import type { ComponentChildren } from 'preact';
import { useLayoutEffect, useState } from 'preact/hooks';
import { type Settings, defaultSettings } from '../../game/account';
import { DEFAULT_BINDINGS, type GameAction } from '../../input/Input';
import { api } from '../../ui/app/api';
import { closePanel, ui } from '../../ui/app/store';
import { Window } from '../../ui/app/Window';
import { extraSettings } from '../economy/saveExt';
import { Toggle, commit, confirmThen, toast, useCtx } from '../economy/ui';
import {
  ACTION_GROUPS,
  LOCKED_ACTIONS,
  codeFromEvent,
  conflicts,
  fullBindings,
  rebind,
} from './keybinds';
import { keyLabel } from '../../input/keyLabel';

type Section = 'video' | 'audio' | 'gameplay' | 'keybinds' | 'account';

const SECTIONS: { id: Section; name: string }[] = [
  { id: 'video', name: 'Video' },
  { id: 'audio', name: 'Audio' },
  { id: 'gameplay', name: 'Gameplay' },
  { id: 'keybinds', name: 'Keybinds' },
  { id: 'account', name: 'Account' },
];

const ACTION_LABEL: Partial<Record<GameAction, string>> = Object.fromEntries(
  ACTION_GROUPS.flatMap((g) => g.actions.map((a) => [a.id, a.label])),
);

function set<K extends keyof Settings>(key: K, value: Settings[K]): void {
  api().setSetting(key, value);
}

function Row({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ComponentChildren;
}) {
  return (
    <div class="st-row">
      <div class="st-row-l">
        <div class="st-label">{label}</div>
        {hint && <div class="st-hint">{hint}</div>}
      </div>
      <div class="st-row-c">{children}</div>
    </div>
  );
}

function Slider({
  value,
  min,
  max,
  step,
  format,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  step: number;
  format: (v: number) => string;
  onChange: (v: number) => void;
}) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <span class="st-slider">
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        style={{ ['--p' as string]: `${pct}%` }}
        onInput={(e) => onChange(Number(e.currentTarget.value))}
      />
      <span class="st-val">{format(value)}</span>
    </span>
  );
}

function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { v: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <span class="st-seg" role="radiogroup">
      {options.map((o) => (
        <button
          key={o.v}
          role="radio"
          aria-checked={value === o.v}
          class={value === o.v ? 'is-on' : ''}
          onClick={() => onChange(o.v)}
        >
          {o.label}
        </button>
      ))}
    </span>
  );
}

const pct = (v: number) => `${Math.round(v * 100)}%`;

const LEVEL_LABEL = { low: 'Low', medium: 'Medium', high: 'High' } as const;

function Video({ s }: { s: Settings }) {
  const hint =
    s.quality === 'auto'
      ? `Adapts to this device and its frame rate. Now: ${LEVEL_LABEL[ui.graphicsLevel.value]}.`
      : 'Shadows, post-processing and draw distance.';
  return (
    <>
      <Row label="Graphics quality" hint={hint}>
        <Segmented
          value={s.quality}
          options={[
            { v: 'auto', label: 'Auto' },
            { v: 'low', label: 'Low' },
            { v: 'medium', label: 'Medium' },
            { v: 'high', label: 'High' },
          ]}
          onChange={(v) => set('quality', v)}
        />
      </Row>
      <Row label="Interface scale" hint="Size of the combat HUD.">
        <Slider
          value={s.hudScale}
          min={0.8}
          max={1.2}
          step={0.05}
          format={pct}
          onChange={(v) => set('hudScale', Math.round(v * 100) / 100)}
        />
      </Row>
      <Row label="Damage numbers">
        <Toggle on={s.showDamage} onChange={(v) => set('showDamage', v)} label="Damage numbers" />
      </Row>
      <Row label="Screen shake" hint="Camera shake on heavy hits.">
        <Slider
          value={s.shake}
          min={0}
          max={1.5}
          step={0.1}
          format={(v) => (v === 0 ? 'Off' : pct(v))}
          onChange={(v) => set('shake', Math.round(v * 10) / 10)}
        />
      </Row>
      <Row label="Reduce flashing" hint="Softens bright flashes from skills and explosions.">
        <Toggle
          on={s.flashReduction}
          onChange={(v) => set('flashReduction', v)}
          label="Reduce flashing"
        />
      </Row>
    </>
  );
}

function Audio({ s }: { s: Settings }) {
  const vol = (key: 'master' | 'music' | 'sfx', label: string, hint?: string) => (
    <Row label={label} hint={hint}>
      <Slider
        value={s[key]}
        min={0}
        max={1}
        step={0.05}
        format={(v) => (v === 0 ? 'Muted' : pct(v))}
        onChange={(v) => set(key, Math.round(v * 100) / 100)}
      />
    </Row>
  );
  return (
    <>
      {vol('master', 'Master volume')}
      {vol('music', 'Music', 'Zone and combat themes.')}
      {vol('sfx', 'Effects', 'Skills, hits, footsteps and interface sounds.')}
    </>
  );
}

function Gameplay({ s }: { s: Settings }) {
  const ctx = useCtx();
  const x = ctx ? extraSettings(ctx.a) : null;
  return (
    <>
      <Row
        label="Swap mouse buttons"
        hint={
          s.swapMouse
            ? 'Left click attacks, right click moves.'
            : 'Left click moves, right click attacks.'
        }
      >
        <Toggle on={s.swapMouse} onChange={(v) => set('swapMouse', v)} label="Swap mouse buttons" />
      </Row>
      <Row
        label="Session days"
        hint={
          s.sessionDays
            ? 'A new day begins when you Rest at the Bounty Board, in town or on Haven Isle.'
            : 'Days reset in real time at 06:00; the week turns on Wednesday.'
        }
      >
        <Toggle on={s.sessionDays} onChange={(v) => set('sessionDays', v)} label="Session days" />
      </Row>
      <Row
        label="Rest anytime"
        hint="Relaxed pace: Rest may start a new day before the daily core is done."
      >
        <Toggle
          on={!!x?.restAnytime}
          onChange={(v) => {
            if (!x) return;
            x.restAnytime = v;
            commit();
          }}
          label="Rest anytime"
        />
      </Row>
      <Row label="Online play" hint="Meet other players in towns, fields and parties.">
        <Toggle on={s.online} onChange={(v) => set('online', v)} label="Online play" />
      </Row>
    </>
  );
}

function Keybinds({ s }: { s: Settings }) {
  const [capturing, setCapturing] = useState<GameAction | null>(null);
  const [note, setNote] = useState<{ text: string; bad?: boolean } | null>(null);
  const b = fullBindings(s.bindings);
  const dupes = conflicts(b);

  useLayoutEffect(() => {
    if (!capturing) return;
    const swallow = (e: Event) => {
      e.preventDefault();
      e.stopImmediatePropagation();
    };
    const onKey = (e: KeyboardEvent) => {
      swallow(e);
      if (e.code === 'Escape') {
        setCapturing(null);
        setNote(null);
        return;
      }
      const code = codeFromEvent(e);
      if (!code) return;
      const r = rebind(fullBindings(api().account.settings.bindings), capturing, code);
      setCapturing(null);
      if (!r.ok) {
        setNote({ text: r.reason, bad: true });
        return;
      }
      set('bindings', r.bindings);
      const me = ACTION_LABEL[capturing] ?? capturing;
      setNote({
        text: r.swapped
          ? `${me} is now ${keyLabel(code)}. ${ACTION_LABEL[r.swapped] ?? r.swapped} moved to ${keyLabel(r.bindings[r.swapped])}.`
          : `${me} is now ${keyLabel(code)}.`,
      });
    };
    const cancel = () => setCapturing(null);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('keyup', swallow, true);
    window.addEventListener('pointerdown', cancel, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('keyup', swallow, true);
      window.removeEventListener('pointerdown', cancel, true);
    };
  }, [capturing]);

  return (
    <div class="st-keys">
      <div class={`st-keynote${note?.bad ? ' is-bad' : ''}`}>
        {capturing
          ? `Press a key for ${ACTION_LABEL[capturing] ?? capturing} — Esc cancels, hold Alt for Alt+ combos.`
          : (note?.text ??
            'Click a key to change it. Taking a key that is already used swaps the two.')}
      </div>
      {ACTION_GROUPS.map((g) => (
        <div key={g.name} class="st-keygroup">
          <div class="section-h">{g.name}</div>
          <div class="st-keygrid">
            {g.actions.map((a) => {
              const locked = LOCKED_ACTIONS.includes(a.id);
              const code = b[a.id];
              const changed = code !== DEFAULT_BINDINGS[a.id];
              return (
                <div key={a.id} class={`st-key${dupes.has(code) ? ' is-dupe' : ''}`}>
                  <span class="st-key-l">{a.label}</span>
                  <button
                    class={`st-cap${capturing === a.id ? ' is-live' : ''}${changed ? ' is-changed' : ''}`}
                    disabled={locked}
                    title={
                      locked
                        ? 'Fixed'
                        : changed
                          ? `Default: ${keyLabel(DEFAULT_BINDINGS[a.id])}`
                          : undefined
                    }
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => {
                      setNote(null);
                      setCapturing(capturing === a.id ? null : a.id);
                    }}
                  >
                    {capturing === a.id ? '…' : keyLabel(code)}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function Account() {
  const [code, setCode] = useState('');
  const [importCode, setImportCode] = useState('');
  const [err, setErr] = useState('');
  return (
    <>
      <Row
        label="Save"
        hint="Your roster saves automatically every 30 seconds and on every important change."
      >
        <button
          class="btn"
          onClick={() => {
            api().save();
            toast('Game saved', '#7bd66b');
          }}
        >
          Save now
        </button>
      </Row>
      <div class="st-block">
        <div class="st-label">Export save code</div>
        <div class="st-hint">Copy this code to move your whole roster to another browser.</div>
        <div class="st-code-row">
          <textarea
            class="st-code"
            readOnly
            value={code}
            placeholder="Press Export to generate your code."
            rows={3}
          />
          <div class="st-code-btns">
            <button class="btn" onClick={() => setCode(api().exportSave())}>
              Export
            </button>
            <button
              class="btn"
              disabled={!code}
              onClick={() => {
                void navigator.clipboard
                  ?.writeText(code)
                  .then(() => toast('Save code copied', '#7bd66b'))
                  .catch(() =>
                    toast('Copy failed — select the code and copy it manually', '#e5484d'),
                  );
              }}
            >
              Copy
            </button>
          </div>
        </div>
      </div>
      <div class="st-block">
        <div class="st-label">Import save code</div>
        <div class="st-hint">
          Replaces this browser's roster with the imported one and returns to character select.
        </div>
        <div class="st-code-row">
          <textarea
            class="st-code"
            value={importCode}
            placeholder="Paste a save code (AF1.…)"
            rows={3}
            onInput={(e) => setImportCode(e.currentTarget.value)}
          />
          <div class="st-code-btns">
            <button
              class="btn btn--gold"
              disabled={!importCode.trim()}
              onClick={() =>
                confirmThen(
                  'Import this save? Your current roster in this browser will be replaced.',
                  'Import',
                  () => {
                    try {
                      api().importSave(importCode);
                      setErr('');
                      api().logout();
                    } catch (e) {
                      setErr(e instanceof Error ? e.message : String(e));
                    }
                  },
                )
              }
            >
              Import
            </button>
          </div>
        </div>
        {err && <div class="st-err">{err}</div>}
      </div>
      <Row label="Character select" hint="Saves and returns to your roster.">
        <button
          class="btn"
          onClick={() =>
            confirmThen('Log out to character select?', 'Log out', () => api().logout())
          }
        >
          Log out
        </button>
      </Row>
    </>
  );
}

function resetSection(sec: Section): void {
  const d = defaultSettings();
  if (sec === 'video') {
    set('quality', d.quality);
    set('hudScale', d.hudScale);
    set('showDamage', d.showDamage);
    set('shake', d.shake);
    set('flashReduction', d.flashReduction);
  } else if (sec === 'audio') {
    for (const k of ['master', 'music', 'sfx'] as const) set(k, d[k]);
  } else if (sec === 'gameplay') {
    set('swapMouse', d.swapMouse);
    set('sessionDays', d.sessionDays);
    extraSettings(api().account).restAnytime = false;
    commit();
  } else if (sec === 'keybinds') {
    set('bindings', { ...DEFAULT_BINDINGS });
  }
}

/** Panel `settings` (Esc). Every change applies immediately. */
export function SettingsPanel() {
  useCtx(); // re-render on game data changes
  const [sec, setSec] = useState<Section>('video');
  let s: Settings;
  try {
    s = api().account.settings;
  } catch {
    return null;
  }
  return (
    <Window
      title="Settings"
      width="min(860px, 96vw)"
      height="min(620px, 88vh)"
      className="fx-win fx-settings"
    >
      <div class="st-body">
        <nav class="st-nav" role="tablist" aria-orientation="vertical">
          {SECTIONS.map((x) => (
            <button
              key={x.id}
              role="tab"
              aria-selected={sec === x.id}
              class={`st-tab${sec === x.id ? ' is-sel' : ''}`}
              onClick={() => setSec(x.id)}
            >
              {x.name}
            </button>
          ))}
        </nav>
        <section class="st-content fx-scroll">
          <h3 class="st-h">{SECTIONS.find((x) => x.id === sec)?.name}</h3>
          {sec === 'video' && <Video s={s} />}
          {sec === 'audio' && <Audio s={s} />}
          {sec === 'gameplay' && <Gameplay s={s} />}
          {sec === 'keybinds' && <Keybinds s={s} />}
          {sec === 'account' && <Account />}
        </section>
      </div>
      <footer class="st-foot">
        {sec !== 'account' ? (
          <button
            class="btn"
            onClick={() =>
              confirmThen(
                `Reset ${SECTIONS.find((x) => x.id === sec)?.name ?? ''} settings to their defaults?`,
                'Reset',
                () => resetSection(sec),
              )
            }
          >
            Reset to defaults
          </button>
        ) : (
          <span />
        )}
        <span class="st-foot-hint">Changes apply immediately.</span>
        <button class="btn btn--gold" onClick={() => closePanel()}>
          Close
        </button>
      </footer>
    </Window>
  );
}
